import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {Button, Detail, EMPHASIS, FlexColumn, Modal, TextArea} from '@forecast-it/design-system';

/** One candidate this control can resolve a pasted id against. */
export interface BulkAddCandidate {
	/** What gets reported to `onApply` once this candidate is recognised. */
	id: string;
	/**
	 * The number an author would actually type. `NaN` when nothing on the candidate decodes to a
	 * usable number — that never equals a pasted token, so the candidate simply never resolves rather
	 * than resolving by accident.
	 */
	numericId: number;
}

interface Props {
	/** Prefix for this control's own hooks: `${dataCy}-open`, `-input`, `-apply`, `-cancel`, `-unknown`. */
	dataCy: string;
	/** The button's own label, and this control's popup title — the mockup gives it only one name. */
	label: string;
	candidates: BulkAddCandidate[];
	disabled?: boolean;
	onApply: (ids: string[]) => void;
}

/**
 * The classic feature-flags page's own bulk-add pattern (`feature_flags.js`'s `getBulkModal` /
 * `getBulkUsersModal`, opened from a plain "Bulk add companies"/"Bulk add users" button): a button
 * that opens a small popup with a paste field, rather than a paste field sitting in the page
 * permanently. Split on commas, resolve each token against `candidates` by its numeric id, add the
 * ones recognised, and report how many were skipped — against the directory rather than sending
 * whatever the author typed, because an id the directory does not know would be written as an
 * audience entry naming nobody.
 *
 * Reused twice by the per-company audience modal — once scoped to every company in the directory,
 * once scoped to the people at whichever company is currently selected — so it takes its candidate
 * list as a prop rather than reading the directory itself.
 */
export const NovaDeployBulkAddModal = ({dataCy, label, candidates, disabled = false, onApply}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [pasteValue, setPasteValue] = useState('');
	const [unknownCount, setUnknownCount] = useState<number | null>(null);

	const closePopup = (): void => {
		setIsOpen(false);
		setPasteValue('');
		setUnknownCount(null);
	};

	const handleApply = (): void => {
		const tokens = pasteValue
			.split(',')
			.map(token => token.trim())
			.filter(token => token.length > 0);

		const resolvedIds = new Set<string>();
		let unresolved = 0;
		tokens.forEach(token => {
			const numericToken = Number(token);
			const candidate = Number.isFinite(numericToken)
				? candidates.find(item => item.numericId === numericToken)
				: undefined;
			if (candidate) {
				resolvedIds.add(candidate.id);
			} else {
				unresolved += 1;
			}
		});

		onApply(Array.from(resolvedIds));
		setUnknownCount(unresolved);
		setPasteValue('');
	};

	return (
		<>
			<Button data-cy={`${dataCy}-open`} emphasis={EMPHASIS.MEDIUM} disabled={disabled} onPress={() => setIsOpen(true)}>
				{label}
			</Button>
			{isOpen ? (
				<Modal onCloseModal={closePopup}>
					<Modal.Title>{label}</Modal.Title>
					<Modal.Content data-cy={`${dataCy}-modal`}>
						<FlexColumn gap={'m'}>
							<TextArea
								data-cy={`${dataCy}-input`}
								value={pasteValue}
								onChange={setPasteValue}
								onBlur={() => undefined}
								isOptional={false}
								height={80}
								placeholder={formatMessage({id: 'nova.deploy.audience.bulkPastePlaceholder'})}
							/>
							{unknownCount !== null && unknownCount > 0 ? (
								<Detail data-cy={`${dataCy}-unknown`}>
									{formatMessage({id: 'nova.deploy.audience.bulkUnknownIds'}, {count: unknownCount})}
								</Detail>
							) : null}
						</FlexColumn>
					</Modal.Content>
					<Modal.Footer>
						<Modal.SecondaryFooterButton dataCy={`${dataCy}-cancel`} onPress={closePopup}>
							{formatMessage({id: 'common.cancel'})}
						</Modal.SecondaryFooterButton>
						<Modal.PrimaryFooterButton
							dataCy={`${dataCy}-apply`}
							disabled={!pasteValue.trim()}
							onPress={handleApply}
						>
							{formatMessage({id: 'common.apply'})}
						</Modal.PrimaryFooterButton>
					</Modal.Footer>
				</Modal>
			) : null}
		</>
	);
};
